import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { adminApi, dispensaries as dApi, api, reportsApi } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Database, RefreshCw, Trash2, Plus, Shield, Building2, Users, Eye, EyeOff, Flame, Wallet, Activity, ScrollText, Crown, Ban, BadgeCheck, Skull, Star, Brain, Package, Edit3, Save, X } from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "users", label: "Users", icon: Users },
  { id: "moderation", label: "Moderation", icon: Eye },
  { id: "sessions", label: "Sessions", icon: Flame },
  { id: "financials", label: "Financials", icon: Wallet },
  { id: "shops", label: "Shops & Deals", icon: Building2 },
  { id: "catalog", label: "Catalog", icon: Package },
  { id: "insights", label: "ML Insights", icon: Brain },
  { id: "audit", label: "Audit Log", icon: ScrollText },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="min-h-screen"><NavBar /><div className="text-center py-20">Loading...</div></div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  if (user.role !== "admin") return <div className="min-h-screen"><NavBar /><div className="max-w-2xl mx-auto py-32 text-center"><h2 className="font-display text-4xl font-extrabold">Admin only</h2></div></div>;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", top: -100, right: -80 }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 relative">
        <span className="sticker sticker-magenta mb-3 inline-block"><Shield size={11} strokeWidth={2.4} /> Super-admin</span>
        <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.92] mt-3 mb-2">Control <span className="text-rainbow">Room.</span></h1>
        <p className="text-[#0B0F0C]/60 mb-6">Total ops: users, moderation, sessions, money, audit trail.</p>

        {/* Tab nav */}
        <div className="flex flex-wrap gap-2 mb-8 border-b-2 border-[#0B0F0C]/10 pb-4" data-testid="admin-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#0B0F0C] text-xs font-bold uppercase tracking-widest transition-all ${
                tab === id ? "bg-[#15FF73] shadow-[3px_3px_0_#0B0F0C]" : "bg-[#FAF6E8] hover:bg-[#FFE74C]/40"
              }`}
            >
              <Icon size={13} strokeWidth={2.4} />
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "users" && <UsersTab />}
        {tab === "moderation" && <ModerationTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "financials" && <FinancialsTab />}
        {tab === "shops" && <ShopsTab />}
        {tab === "catalog" && <CatalogTab />}
        {tab === "insights" && <InsightsTab />}
        {tab === "audit" && <AuditTab />}
      </div>
      <Footer />
    </div>
  );
}

// ============ Overview ============
function OverviewTab() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [o, s] = await Promise.all([adminApi.overview(), adminApi.stats()]);
      setData(o); setStats(s);
    } catch { toast.error("Failed to load overview"); }
  };
  useEffect(() => { load(); }, []);

  const reseed = async () => { setBusy(true); try { await adminApi.reseed(true); await load(); toast.success("Re-seeded"); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const clearSeed = async () => { setBusy(true); try { await adminApi.clearSeed(); await load(); toast.success("Cleared"); } catch { toast.error("Failed"); } finally { setBusy(false); } };

  if (!data) return <div className="text-center py-20">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-overview-tab">
      {/* User pillars */}
      <Section title="Users" overline="Audience">
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Stat label="Consumers" value={data.users.consumers} tone="card-green" testid="stat-consumers" />
          <Stat label="Dispensary owners" value={data.users.dispensary_owners} tone="card-cyan" testid="stat-disp-owners" />
          <Stat label="Admins" value={data.users.admins} tone="card-purple" testid="stat-admins" />
          <Stat label="Banned" value={data.users.banned} tone="card-magenta" testid="stat-banned" highlight />
          <Stat label="Soft-deleted" value={data.users.deleted} tone="card-sunset" testid="stat-deleted" />
        </div>
      </Section>

      {/* Content & sessions */}
      <Section title="Content & Sessions" overline="Engagement">
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Stat label="Posts (24h)" value={data.content.posts_today} tone="card-green" testid="stat-posts-today" />
          <Stat label="Posts (7d)" value={data.content.posts_week} tone="card-cyan" testid="stat-posts-week" />
          <Stat label="Hidden" value={data.content.hidden_posts} tone="card-magenta" testid="stat-hidden" />
          <Stat label="Active sessions" value={data.sessions.active} tone="card-sunset" testid="stat-active-sessions" highlight />
          <Stat label="Total sessions" value={data.sessions.total_ever} tone="card-purple" testid="stat-total-sessions" />
        </div>
      </Section>

      {/* Money */}
      <Section title="Money flow" overline="Revenue">
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Stat label="Wallet pool" value={`$${data.money.total_wallet_balance.toFixed(0)}`} tone="card-green" testid="stat-wallet-pool" highlight />
          <Stat label="Auto-paused" value={data.money.auto_paused_shops} tone="card-magenta" testid="stat-paused" />
          <Stat label="Tips count" value={data.money.tips_count} tone="card-cyan" testid="stat-tips-count" />
          <Stat label="Tips volume" value={`$${data.money.tips_volume.toFixed(0)}`} tone="card-sunset" testid="stat-tips-volume" />
          <Stat label="Claims (24h)" value={data.activity.claims_today} tone="card-purple" testid="stat-claims" />
        </div>
      </Section>

      {/* Catalog */}
      {stats && (
        <Section title="Catalog" overline="Inventory">
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <Stat label="Shops" value={stats.dispensaries} sub={`${stats.dispensaries_seeded} seed`} tone="card-cyan" testid="stat-shops" />
            <Stat label="Products" value={stats.products} sub={`${stats.products_seeded} seed`} tone="card-sunset" testid="stat-products" />
            <Stat label="Deals" value={stats.deals} sub={`${stats.deals_seeded} seed`} tone="card-magenta" testid="stat-deals" />
            <Stat label="Reviews" value={stats.reviews} tone="card-green" testid="stat-reviews" />
            <Stat label="Referrals" value={stats.referrals} tone="card-purple" testid="stat-referrals" />
          </div>
        </Section>
      )}

      {/* Seed controls */}
      <div className="card-x card-green bg-[#FAF6E8] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="label-overline text-[#FF1A8C] mb-1 flex items-center gap-2"><Database size={11} strokeWidth={2.4} /> Seed data</div>
          <p className="text-sm text-[#0B0F0C]/65 max-w-lg">Pre-populated demo shops, products, deals. Wipe when ready to onboard real partners.</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="reseed-btn" disabled={busy} onClick={reseed} className="btn-x btn-ghost !py-2 !px-4 !text-xs disabled:opacity-50"><RefreshCw size={12} strokeWidth={2.4} className={busy ? "animate-spin" : ""} /> Re-seed</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="clear-seed-btn" className="btn-x btn-magenta !py-2 !px-4 !text-xs"><Trash2 size={12} strokeWidth={2.4} /> Clear seed</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl border-2 border-[#0B0F0C] bg-[#FAF6E8] shadow-[6px_6px_0_#0B0F0C]">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-3xl font-extrabold">Clear all seeded data?</AlertDialogTitle>
                <AlertDialogDescription>Removes only seed records. Real (admin-added) shops + products stay.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full border-2 border-[#0B0F0C]">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-clear-seed" onClick={clearSeed} className="rounded-full border-2 border-[#0B0F0C] bg-[#FF1A8C] hover:bg-[#FF1A8C]/90 text-white">Yes, clear</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

// ============ Users ============
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter !== "all") params.role = roleFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await adminApi.users(params);
      setUsers(r.users);
    } catch { toast.error("Failed to load users"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter, statusFilter]);

  const ban = async (id, handle) => {
    const reason = window.prompt(`Ban @${handle}? Enter reason:`);
    if (reason === null) return;
    try { await adminApi.banUser(id, reason); toast.success(`Banned @${handle}`); load(); } catch { toast.error("Failed"); }
  };
  const unban = async (id, handle) => { try { await adminApi.unbanUser(id); toast.success(`Unbanned @${handle}`); load(); } catch { toast.error("Failed"); } };
  const resetStreak = async (id, handle) => { try { await adminApi.resetStreak(id); toast.success(`Streak reset for @${handle}`); load(); } catch { toast.error("Failed"); } };
  const setRole = async (id, role, handle) => { try { await adminApi.setRole(id, role); toast.success(`@${handle} → ${role}`); load(); } catch { toast.error("Failed"); } };
  const hardDelete = async (id, handle) => { try { await adminApi.hardDeleteUser(id); toast.success(`Deleted @${handle}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <input data-testid="user-search" placeholder="Search handle…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full px-4 h-10 text-sm w-64 outline-none focus:shadow-[3px_3px_0_#0B0F0C]" />
        <Sel testid="user-role-filter" value={roleFilter} on={setRoleFilter} options={["all", "consumer", "dispensary", "admin"]} />
        <Sel testid="user-status-filter" value={statusFilter} on={setStatusFilter} options={["all", "active", "banned", "deleted"]} />
        <button data-testid="user-search-btn" onClick={load} className="btn-x btn-primary !py-2 !px-4 !text-xs">Search</button>
        <span className="text-xs text-[#0B0F0C]/60 font-mono-x font-bold">{users.length} users</span>
      </div>

      <div className="card-x card-cyan bg-[#FAF6E8] p-2 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
            <tr><th className="p-2">Handle</th><th>Role</th><th>XP</th><th>Streak</th><th>Status</th><th>Created</th><th className="text-right pr-2">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-[#0B0F0C]/10 hover:bg-[#FFE74C]/20">
                <td className="p-2 font-display font-bold">@{u.handle || "(none)"}</td>
                <td className="font-mono-x text-xs uppercase">{u.role}</td>
                <td>{u.xp}</td>
                <td>{u.streak_days}d</td>
                <td>
                  {u.deleted_at ? <span className="sticker sticker-magenta">deleted</span> :
                   u.banned_at ? <span className="sticker sticker-magenta" title={u.ban_reason}>banned</span> :
                   <span className="sticker sticker-green">active</span>}
                </td>
                <td className="text-[10px] text-[#0B0F0C]/55 font-mono-x">{(u.created_at || "").slice(0, 10)}</td>
                <td className="text-right pr-2 space-x-1">
                  {!u.deleted_at && (u.banned_at
                    ? <button data-testid={`unban-${u.id}`} onClick={() => unban(u.id, u.handle)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><BadgeCheck size={11} /> Unban</button>
                    : <button data-testid={`ban-${u.id}`} onClick={() => ban(u.id, u.handle)} className="btn-x !py-1 !px-3 !text-[10px] !bg-[#FF1A8C] !text-white"><Ban size={11} /> Ban</button>
                  )}
                  <button data-testid={`reset-streak-${u.id}`} onClick={() => resetStreak(u.id, u.handle)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]" title="Reset streak"><Flame size={11} /></button>
                  <UserRoleMenu user={u} onSet={setRole} />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`del-${u.id}`} className="btn-x btn-ghost !py-1 !px-3 !text-[10px] hover:!text-[#FF1A8C]" title="Hard delete"><Trash2 size={11} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl font-extrabold">Hard-delete @{u.handle}?</AlertDialogTitle>
                        <AlertDialogDescription>Removes user + posts + claims + preorders + notifications. Irreversible.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full border-2 border-[#0B0F0C]">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-del-${u.id}`} onClick={() => hardDelete(u.id, u.handle)} className="rounded-full border-2 border-[#0B0F0C] bg-[#FF1A8C] text-white">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="7" className="text-center text-[#0B0F0C]/50 p-6">No users match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserRoleMenu({ user, onSet }) {
  return (
    <Select value={user.role} onValueChange={(v) => v !== user.role && onSet(user.id, v, user.handle)}>
      <SelectTrigger data-testid={`role-menu-${user.id}`} className="inline-flex w-auto rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] h-7 px-3 text-[10px] font-bold">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
        <SelectItem value="consumer">consumer</SelectItem>
        <SelectItem value="dispensary">dispensary</SelectItem>
        <SelectItem value="admin">admin</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ============ Moderation ============
function ModerationTab() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reports, setReports] = useState([]);
  const [reportFilter, setReportFilter] = useState("pending");

  const load = async () => {
    try {
      const [pp, rr] = await Promise.all([
        adminApi.posts(filter !== "all" ? filter : undefined),
        reportsApi.adminList(reportFilter !== "all" ? reportFilter : undefined),
      ]);
      setPosts(pp.posts);
      setReports(rr.reports);
    } catch { toast.error("Failed to load posts"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, reportFilter]);

  const hide = async (id) => {
    const reason = window.prompt("Reason for hiding (visible in audit log):");
    if (reason === null) return;
    try { await adminApi.hidePost(id, reason); toast.success("Hidden"); load(); } catch { toast.error("Failed"); }
  };
  const unhide = async (id) => { try { await adminApi.unhidePost(id); toast.success("Restored"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await adminApi.deletePost(id); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const resolve = async (rid, action) => {
    try { await reportsApi.adminResolve(rid, action); toast.success(action === "reviewed" ? "Marked reviewed" : "Dismissed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-moderation-tab">
      {/* User Reports queue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-overline text-[#FF1A8C] mb-1 flex items-center gap-2"><Eye size={11} strokeWidth={2.4} /> User reports</div>
            <h3 className="font-display text-2xl font-extrabold">Flagged content {reports.length > 0 && <span className="sticker sticker-magenta ml-2">{reports.length}</span>}</h3>
          </div>
          <Sel testid="report-filter" value={reportFilter} on={setReportFilter} options={["pending", "all", "reviewed", "dismissed"]} />
        </div>
        <div className="card-x card-magenta bg-[#FAF6E8] p-2 overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
              <tr><th className="p-2">When</th><th>Reporter</th><th>Type</th><th>Target preview</th><th>Reason</th><th>Status</th><th className="text-right pr-2">Actions</th></tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-t border-[#0B0F0C]/10 hover:bg-[#FFE74C]/20">
                  <td className="p-2 text-[10px] font-mono-x">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="font-display font-bold text-xs">@{r.reporter_handle}</td>
                  <td><span className="sticker !text-[9px]">{r.target_type}</span></td>
                  <td className="text-xs max-w-md truncate" title={r.target_preview}>{r.target_preview}</td>
                  <td className="text-xs italic">{r.reason || "(no reason)"}</td>
                  <td>
                    {r.status === "pending" ? <span className="sticker sticker-magenta !text-[9px]">PENDING</span> :
                     r.status === "reviewed" ? <span className="sticker sticker-green !text-[9px]">reviewed</span> :
                     <span className="sticker !text-[9px]">dismissed</span>}
                  </td>
                  <td className="text-right pr-2 space-x-1">
                    {r.status === "pending" && (
                      <>
                        <button data-testid={`report-review-${r.id}`} onClick={() => resolve(r.id, "reviewed")} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><BadgeCheck size={11} /> Reviewed</button>
                        <button data-testid={`report-dismiss-${r.id}`} onClick={() => resolve(r.id, "dismissed")} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]">Dismiss</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {reports.length === 0 && <tr><td colSpan="7" className="text-center text-[#0B0F0C]/50 p-6">No reports.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Post moderation grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-overline text-[#FF1A8C] mb-1 flex items-center gap-2">All posts</div>
            <h3 className="font-display text-2xl font-extrabold">Post moderation</h3>
          </div>
          <div className="flex gap-3 items-center">
            <Sel testid="post-filter" value={filter} on={setFilter} options={["all", "visible", "hidden"]} />
            <span className="text-xs text-[#0B0F0C]/60 font-mono-x font-bold">{posts.length} posts</span>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p) => (
          <div key={p.id} data-testid={`mod-post-${p.id}`} className={`card-x bg-[#FAF6E8] p-4 ${p.hidden ? "card-magenta opacity-70" : "card-green"}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-display font-bold">@{p.user_handle}</span>
              {p.verdict && <span className="sticker sticker-green !text-[9px]">{p.verdict.toUpperCase()}</span>}
            </div>
            {p.has_image && <div className="aspect-video rounded-xl border-2 border-[#0B0F0C] bg-[#0B0F0C]/5 flex items-center justify-center text-xs text-[#0B0F0C]/40 mb-2">[image attached]</div>}
            <p className="text-sm text-[#0B0F0C]/80 line-clamp-3 mb-2">{p.caption || "(no caption)"}</p>
            <div className="text-[10px] text-[#0B0F0C]/55 font-mono-x font-bold uppercase tracking-widest mb-3">
              {p.like_count} likes · {p.comment_count} comments · {(p.created_at || "").slice(0, 16)}
              {p.hidden && <div className="text-[#FF1A8C] mt-1">HIDDEN: {p.hidden_reason || "(no reason)"}</div>}
            </div>
            <div className="flex gap-2">
              {p.hidden
                ? <button data-testid={`unhide-${p.id}`} onClick={() => unhide(p.id)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><Eye size={11} /> Restore</button>
                : <button data-testid={`hide-${p.id}`} onClick={() => hide(p.id)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><EyeOff size={11} /> Hide</button>
              }
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid={`del-post-${p.id}`} className="btn-x !py-1 !px-3 !text-[10px] !bg-[#FF1A8C] !text-white"><Trash2 size={11} /> Delete</button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                    <AlertDialogDescription>Permanent. Use Hide instead if you might restore.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full border-2 border-[#0B0F0C]">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid={`confirm-del-post-${p.id}`} onClick={() => del(p.id)} className="rounded-full border-2 border-[#0B0F0C] bg-[#FF1A8C] text-white">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="col-span-full text-center text-[#0B0F0C]/50 p-12">No posts to moderate.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ Sessions ============
function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = async () => { try { const r = await adminApi.sessions(filter !== "all" ? filter : undefined); setSessions(r.sessions); } catch { toast.error("Failed"); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const kill = async (sid, title) => { try { await adminApi.killSession(sid); toast.success(`Detonated "${title}"`); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-4" data-testid="admin-sessions-tab">
      <div className="flex gap-3 items-center">
        <Sel testid="session-filter" value={filter} on={setFilter} options={["all", "active", "closed", "expired"]} />
        <span className="text-xs text-[#0B0F0C]/60 font-mono-x font-bold">{sessions.length} sessions</span>
      </div>
      <div className="card-x card-purple bg-[#FAF6E8] p-2 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
            <tr><th className="p-2">Title</th><th>Host</th><th>Topic</th><th>State</th><th>Visibility</th><th>Members</th><th>Msgs</th><th>Expires</th><th>Status</th><th className="pr-2 text-right">Action</th></tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} data-testid={`session-row-${s.id}`} className="border-t border-[#0B0F0C]/10 hover:bg-[#FFE74C]/20">
                <td className="p-2 font-display font-bold max-w-[200px] truncate" title={s.title}>{s.title}</td>
                <td className="text-xs">@{s.host_handle}</td>
                <td className="text-xs"><span className="sticker sticker-green !text-[9px]">{s.topic}</span></td>
                <td className="text-xs">{s.state || "-"}</td>
                <td className="text-xs uppercase font-mono-x">{s.visibility}</td>
                <td>{s.member_count}</td>
                <td>{s.message_count}</td>
                <td className="text-[10px] text-[#0B0F0C]/55 font-mono-x">{(s.expires_at || "").slice(0, 16)}</td>
                <td>
                  {s.killed_by_admin ? <span className="sticker sticker-magenta !text-[9px]">killed</span> :
                   s.closed ? <span className="sticker !text-[9px]">closed</span> :
                   (s.expires_at || "") < new Date().toISOString() ? <span className="sticker !text-[9px]">expired</span> :
                   <span className="sticker sticker-green !text-[9px]">live</span>}
                </td>
                <td className="text-right pr-2">
                  {!s.closed && (s.expires_at || "") > new Date().toISOString() && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`kill-${s.id}`} className="btn-x !py-1 !px-3 !text-[10px] !bg-[#FF1A8C] !text-white"><Skull size={11} /> Kill</button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-3xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Detonate this session?</AlertDialogTitle>
                          <AlertDialogDescription>Purges all messages, revokes encryption key, marks as killed. Irreversible.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-full border-2 border-[#0B0F0C]">Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid={`confirm-kill-${s.id}`} onClick={() => kill(s.id, s.title)} className="rounded-full border-2 border-[#0B0F0C] bg-[#FF1A8C] text-white">Detonate</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
            {sessions.length === 0 && <tr><td colSpan="10" className="text-center text-[#0B0F0C]/50 p-6">No sessions in this view.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ Financials ============
function FinancialsTab() {
  const [data, setData] = useState(null);
  const load = async () => { try { setData(await adminApi.wallets()); } catch { toast.error("Failed"); } };
  useEffect(() => { load(); }, []);

  const adjust = async (dispId, name) => {
    const raw = window.prompt(`Adjust wallet for ${name}.\nEnter amount (positive = credit, negative = debit):`);
    if (!raw) return;
    const amt = parseFloat(raw);
    if (Number.isNaN(amt)) { toast.error("Bad number"); return; }
    const note = window.prompt("Note (audit log):") || "";
    try { await adminApi.walletAdjust(dispId, amt, note); toast.success(`Adjusted ${amt > 0 ? "+" : ""}$${amt}`); load(); } catch { toast.error("Failed"); }
  };

  if (!data) return <div className="text-center py-20">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="admin-financials-tab">
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <Stat label="Total wallet pool" value={`$${data.total_wallet_balance.toFixed(2)}`} tone="card-green" highlight testid="fin-total-pool" />
        <Stat label="Shop count" value={data.shop_count} tone="card-cyan" testid="fin-shop-count" />
        <Stat label="Auto-paused" value={data.wallets.filter((w) => w.auto_paused).length} tone="card-magenta" testid="fin-paused-count" />
      </div>

      <div className="card-x card-sunset bg-[#FAF6E8] p-2 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
            <tr><th className="p-2">Shop</th><th>Location</th><th>Balance</th><th>Deposits</th><th>Commissions</th><th>Rate</th><th>Status</th><th className="text-right pr-2">Action</th></tr>
          </thead>
          <tbody>
            {data.wallets.map((w) => (
              <tr key={w.id} data-testid={`wallet-row-${w.id}`} className="border-t border-[#0B0F0C]/10 hover:bg-[#FFE74C]/20">
                <td className="p-2 font-display font-bold">{w.name}</td>
                <td className="text-xs">{w.city}, {w.state}</td>
                <td className="font-mono-x font-bold">${w.wallet_balance.toFixed(2)}</td>
                <td className="text-xs">${(w.deposits_total || 0).toFixed(2)}</td>
                <td className="text-xs">${(w.commissions_total || 0).toFixed(2)}</td>
                <td className="text-xs">{w.commission_rate_pct}% + ${w.commission_flat}</td>
                <td>{w.auto_paused ? <span className="sticker sticker-magenta !text-[9px]">PAUSED</span> : <span className="sticker sticker-green !text-[9px]">live</span>}</td>
                <td className="text-right pr-2">
                  <button data-testid={`adjust-${w.id}`} onClick={() => adjust(w.id, w.name)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><Wallet size={11} /> Adjust</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ Shops & Deals ============
function ShopsTab() {
  const [disps, setDisps] = useState([]);
  const [deals, setDeals] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", state: "TX", type: "recreational", address: "", description: "", image_url: "https://images.unsplash.com/photo-1764795849755-ab58c8fef307?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" });

  const load = async () => {
    try { const [d, dl] = await Promise.all([dApi.list(), adminApi.deals()]); setDisps(d); setDeals(dl.deals); }
    catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const addDisp = async () => {
    if (!form.name || !form.city) { toast.error("Name + City required"); return; }
    try { await dApi.create(form); setForm({ ...form, name: "", address: "", description: "" }); load(); toast.success("Real shop added"); }
    catch { toast.error("Failed"); }
  };
  const removeDisp = async (id) => { await dApi.remove(id); load(); toast.success("Removed"); };
  const featureDeal = async (id, title) => { try { const r = await adminApi.featureDeal(id); toast.success(`${r.is_featured ? "Featured" : "Unfeatured"}: ${title}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-shops-tab">
      <div className="lg:col-span-2 space-y-6">
        {/* Shops */}
        <div className="card-x card-cyan bg-[#FAF6E8] p-6">
          <div className="label-overline text-[#FF1A8C] mb-4 flex items-center gap-2"><Building2 size={11} strokeWidth={2.4} /> Shops · {disps.length}</div>
          <div className="space-y-2 max-h-[360px] overflow-auto pr-2">
            {disps.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-3 hover:bg-[#FFE74C]/20">
                <img src={d.image_url} alt="" className="w-12 h-12 object-cover rounded-xl border-2 border-[#0B0F0C]" />
                <div className="flex-1">
                  <div className="font-display font-bold flex items-center gap-2">{d.name}{d.is_seeded ? <span className="sticker !text-[9px] !py-0.5">seed</span> : <span className="sticker sticker-green !text-[9px] !py-0.5">real</span>}</div>
                  <div className="text-[10px] text-[#0B0F0C]/55 font-mono-x font-bold uppercase tracking-widest">{d.city}, {d.state} · {d.type}</div>
                </div>
                <button data-testid={`admin-del-disp-${d.id}`} onClick={() => removeDisp(d.id)} className="text-[#0B0F0C]/55 hover:text-[#FF1A8C] p-2"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Deals */}
        <div className="card-x card-magenta bg-[#FAF6E8] p-6">
          <div className="label-overline text-[#FF1A8C] mb-4 flex items-center gap-2"><Star size={11} strokeWidth={2.4} /> Deals · {deals.length}</div>
          <div className="space-y-2 max-h-[420px] overflow-auto pr-2">
            {deals.map((d) => (
              <div key={d.id} data-testid={`deal-row-${d.id}`} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-3 hover:bg-[#FFE74C]/20">
                <div className="flex-1">
                  <div className="font-display font-bold flex items-center gap-2">{d.title}
                    {d.is_featured && <span className="sticker sticker-green !text-[9px]"><Crown size={9} /> FEATURED</span>}
                    {d.is_flash && <span className="sticker sticker-magenta !text-[9px]">FLASH</span>}
                    {d.expired && <span className="sticker !text-[9px]">expired</span>}
                  </div>
                  <div className="text-[10px] text-[#0B0F0C]/55 font-mono-x font-bold uppercase tracking-widest">{d.dispensary_name} · {d.discount_pct}% off · {d.claim_count} claims</div>
                </div>
                <button data-testid={`feature-${d.id}`} onClick={() => featureDeal(d.id, d.title)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]">{d.is_featured ? "Unfeature" : "Feature"}</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-x card-sunset bg-[#FAF6E8] p-6 h-fit">
        <div className="label-overline text-[#FF1A8C] mb-4">+ Add real shop</div>
        <div className="space-y-3">
          <Field placeholder="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="adm-name" />
          <Field placeholder="City" v={form.city} on={(v) => setForm({ ...form, city: v })} testid="adm-city" />
          <div className="grid grid-cols-2 gap-2">
            <Sel value={form.state} on={(v) => setForm({ ...form, state: v })} options={["TX", "CO", "CA", "NV"]} testid="adm-state" />
            <Sel value={form.type} on={(v) => setForm({ ...form, type: v })} options={["recreational", "medical", "hemp"]} testid="adm-type" />
          </div>
          <Field placeholder="Address" v={form.address} on={(v) => setForm({ ...form, address: v })} testid="adm-address" />
          <Field placeholder="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} testid="adm-desc" />
          <Field placeholder="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} testid="adm-img" />
          <button data-testid="adm-add" onClick={addDisp} className="btn-x btn-primary w-full !text-sm"><Plus size={14} strokeWidth={2.4} /> Add</button>
        </div>
      </div>
    </div>
  );
}

// ============ Audit Log ============
function AuditTab() {
  const [entries, setEntries] = useState([]);
  const load = async () => { try { const r = await adminApi.auditLog(200); setEntries(r.entries); } catch { toast.error("Failed"); } };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="admin-audit-tab">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#0B0F0C]/60 font-mono-x font-bold">{entries.length} entries (newest first)</span>
        <button data-testid="audit-refresh" onClick={load} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><RefreshCw size={11} /> Refresh</button>
      </div>
      <div className="card-x card-purple bg-[#FAF6E8] p-2">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
            <tr><th className="p-2">When</th><th>Admin</th><th>Action</th><th>Target</th><th>Detail</th></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-[#0B0F0C]/10">
                <td className="p-2 text-[10px] font-mono-x">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="font-display font-bold text-xs">@{e.admin_handle}</td>
                <td><span className="sticker sticker-magenta !text-[9px]">{e.action}</span></td>
                <td className="text-xs font-mono-x">{e.target_type}/{(e.target_id || "").slice(0, 8)}</td>
                <td className="text-[10px] text-[#0B0F0C]/70 max-w-md truncate" title={JSON.stringify(e.payload)}>{Object.entries(e.payload || {}).map(([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 40) : v}`).join(" · ")}</td>
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan="5" className="text-center text-[#0B0F0C]/50 p-6">No admin actions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ Catalog (full Product + Deal CRUD) ============
const CATEGORIES = ["flower", "vape", "edible", "concentrate", "tincture", "preroll", "holistic", "mushroom"];
const STRAINS = ["indica", "sativa", "hybrid", "cbd", "herb"];
const SUBTYPES_VAPE = ["none", "cart", "pod", "disposable", "battery"];
const SUBTYPES_MUSHROOM = ["none", "amanita", "functional", "microdose"];
const EFFECT_TAGS = ["relax", "energy", "focus", "sleep", "creative", "euphoric"];
const FLAVOR_TAGS = ["earthy", "pine", "citrus", "sweet", "berry", "diesel", "mint", "vanilla", "spice", "floral", "mango", "sour", "bitter"];

function CatalogTab() {
  const [tab, setTab] = useState("products");
  return (
    <div className="space-y-4" data-testid="admin-catalog-tab">
      <div className="flex gap-2">
        <button data-testid="cat-tab-products" onClick={() => setTab("products")} className={`btn-x !text-xs ${tab === "products" ? "btn-primary" : "btn-ghost"}`}>Products</button>
        <button data-testid="cat-tab-deals" onClick={() => setTab("deals")} className={`btn-x !text-xs ${tab === "deals" ? "btn-primary" : "btn-ghost"}`}>Deals</button>
      </div>
      {tab === "products" && <ProductCRUD />}
      {tab === "deals" && <DealCRUD />}
    </div>
  );
}

function ProductCRUD() {
  const [products, setProducts] = useState([]);
  const [disps, setDisps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [d, p] = await Promise.all([dApi.list(), api.get("/products?limit=500").then((r) => r.data)]);
      setDisps(d); setProducts(p);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = products.filter((p) => {
    if (filterCat !== "all" && p.category !== filterCat) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase()) && !(p.brand || "").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const save = async (data) => {
    try {
      if (editing?.id) {
        await adminApi.updateProduct(editing.id, data);
        toast.success("Saved");
      } else {
        await api.post("/products", data);
        toast.success("Created");
      }
      setEditing(null); setCreating(false);
      load();
    } catch (e) { toast.error("Save failed: " + (e?.response?.data?.detail || e.message)); }
  };

  const remove = async (id, name) => {
    try { await adminApi.deleteProduct(id); toast.success(`Deleted ${name}`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center">
        <input data-testid="cat-product-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full px-4 h-10 text-sm w-64 outline-none" />
        <Sel testid="cat-product-cat-filter" value={filterCat} on={setFilterCat} options={["all", ...CATEGORIES]} />
        <span className="text-xs text-[#0B0F0C]/60 font-mono-x font-bold">{filtered.length} / {products.length}</span>
        <button data-testid="cat-product-new" onClick={() => { setCreating(true); setEditing({}); }} className="btn-x btn-primary !text-xs ml-auto"><Plus size={12} /> New product</button>
      </div>
      <div className="card-x card-green bg-[#FAF6E8] p-2 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
            <tr><th className="p-2">Name</th><th>Category</th><th>Subtype</th><th>Strain</th><th>THC</th><th>Price</th><th>Top</th><th className="text-right pr-2">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((p) => (
              <tr key={p.id} data-testid={`cat-product-row-${p.id}`} className="border-t border-[#0B0F0C]/10 hover:bg-[#FFE74C]/20">
                <td className="p-2 font-display font-bold">{p.name}<div className="text-[10px] text-[#0B0F0C]/55 font-mono-x">{p.brand}</div></td>
                <td><span className="sticker sticker-green !text-[9px]">{p.category}</span></td>
                <td className="text-xs">{p.subtype || "-"}</td>
                <td className="text-xs uppercase font-mono-x">{p.strain}</td>
                <td>{p.thc}%</td>
                <td className="font-bold">${p.price}</td>
                <td>{p.is_top_shelf ? <Crown size={11} className="text-[#FFD500]" /> : ""}</td>
                <td className="text-right pr-2 space-x-1">
                  <button data-testid={`cat-edit-${p.id}`} onClick={() => setEditing(p)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><Edit3 size={11} /></button>
                  <button data-testid={`cat-del-${p.id}`} onClick={() => remove(p.id, p.name)} className="btn-x !py-1 !px-3 !text-[10px] !bg-[#FF1A8C] !text-white"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(editing || creating) && <ProductFormModal product={editing} disps={disps} onSave={save} onClose={() => { setEditing(null); setCreating(false); }} />}
    </div>
  );
}

function ProductFormModal({ product, disps, onSave, onClose }) {
  const [f, setF] = useState({
    dispensary_id: product?.dispensary_id || disps[0]?.id || "",
    name: product?.name || "",
    brand: product?.brand || "",
    category: product?.category || "flower",
    subtype: product?.subtype || "none",
    strain: product?.strain || "hybrid",
    thc: product?.thc ?? 0,
    cbd: product?.cbd ?? 0,
    price: product?.price ?? 0,
    weight_g: product?.weight_g ?? 3.5,
    image_url: product?.image_url || "",
    effects: product?.effects || [],
    flavors: product?.flavors || [],
    is_top_shelf: product?.is_top_shelf ?? false,
    is_sleeper_pick: product?.is_sleeper_pick ?? false,
  });
  const subtypes = f.category === "vape" ? SUBTYPES_VAPE : f.category === "mushroom" ? SUBTYPES_MUSHROOM : null;
  // Reset subtype to "none" when category changes to one without subtypes
  useEffect(() => {
    if (!subtypes && f.subtype !== "none") setF((s) => ({ ...s, subtype: "none" }));
    // eslint-disable-next-line
  }, [f.category]);
  const toggleTag = (key, tag) => {
    setF((s) => ({ ...s, [key]: s[key].includes(tag) ? s[key].filter((x) => x !== tag) : [...s[key], tag] }));
  };
  const submit = () => {
    if (!f.name || !f.dispensary_id) { toast.error("Name + shop required"); return; }
    const subtype = f.subtype === "none" ? null : f.subtype;
    onSave({ ...f, subtype, thc: Number(f.thc), cbd: Number(f.cbd), price: Number(f.price), weight_g: Number(f.weight_g) });
  };
  return (
    <div className="fixed inset-0 bg-[#0B0F0C]/60 z-50 flex items-center justify-center p-4 overflow-auto" data-testid="product-form-modal">
      <div className="card-x card-green bg-[#FAF6E8] p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-3xl font-extrabold">{product?.id ? "Edit" : "New"} product</h3>
          <button onClick={onClose} className="text-[#0B0F0C]/60 hover:text-[#FF1A8C]"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <FormRow label="Name"><Field testid="pf-name" placeholder="Product name" v={f.name} on={(v) => setF({ ...f, name: v })} /></FormRow>
          <FormRow label="Brand"><Field testid="pf-brand" placeholder="Brand" v={f.brand} on={(v) => setF({ ...f, brand: v })} /></FormRow>
          <FormRow label="Shop"><Sel testid="pf-shop" value={f.dispensary_id} on={(v) => setF({ ...f, dispensary_id: v })} options={disps.map((d) => ({ v: d.id, l: `${d.name} · ${d.state}` }))} /></FormRow>
          <FormRow label="Category"><Sel testid="pf-category" value={f.category} on={(v) => setF({ ...f, category: v, subtype: "" })} options={CATEGORIES} /></FormRow>
          {subtypes && (<FormRow label="Subtype"><Sel testid="pf-subtype" value={f.subtype} on={(v) => setF({ ...f, subtype: v })} options={subtypes.map((s) => ({ v: s, l: s === "none" ? "(none)" : s }))} /></FormRow>)}
          <FormRow label="Strain"><Sel testid="pf-strain" value={f.strain} on={(v) => setF({ ...f, strain: v })} options={STRAINS} /></FormRow>
          <FormRow label="THC %"><Field testid="pf-thc" placeholder="0" v={f.thc} on={(v) => setF({ ...f, thc: v })} type="number" /></FormRow>
          <FormRow label="CBD %"><Field testid="pf-cbd" placeholder="0" v={f.cbd} on={(v) => setF({ ...f, cbd: v })} type="number" /></FormRow>
          <FormRow label="Price $"><Field testid="pf-price" placeholder="0" v={f.price} on={(v) => setF({ ...f, price: v })} type="number" /></FormRow>
          <FormRow label="Weight (g)"><Field testid="pf-weight" placeholder="3.5" v={f.weight_g} on={(v) => setF({ ...f, weight_g: v })} type="number" /></FormRow>
        </div>
        <FormRow label="Image URL"><Field testid="pf-img" placeholder="https://..." v={f.image_url} on={(v) => setF({ ...f, image_url: v })} /></FormRow>
        <FormRow label="Effects">
          <div className="flex flex-wrap gap-1.5">
            {EFFECT_TAGS.map((t) => <TagBtn key={t} tag={t} active={f.effects.includes(t)} onClick={() => toggleTag("effects", t)} testid={`pf-effect-${t}`} />)}
          </div>
        </FormRow>
        <FormRow label="Flavors">
          <div className="flex flex-wrap gap-1.5">
            {FLAVOR_TAGS.map((t) => <TagBtn key={t} tag={t} active={f.flavors.includes(t)} onClick={() => toggleTag("flavors", t)} testid={`pf-flavor-${t}`} />)}
          </div>
        </FormRow>
        <div className="flex gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm"><input data-testid="pf-topshelf" type="checkbox" checked={f.is_top_shelf} onChange={(e) => setF({ ...f, is_top_shelf: e.target.checked })} /> Top shelf</label>
          <label className="flex items-center gap-2 text-sm"><input data-testid="pf-sleeper" type="checkbox" checked={f.is_sleeper_pick} onChange={(e) => setF({ ...f, is_sleeper_pick: e.target.checked })} /> Sleeper pick</label>
        </div>
        <div className="flex gap-3 mt-5">
          <button data-testid="pf-cancel" onClick={onClose} className="btn-x btn-ghost flex-1 !text-sm">Cancel</button>
          <button data-testid="pf-save" onClick={submit} className="btn-x btn-primary flex-1 !text-sm"><Save size={14} /> {product?.id ? "Save" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function DealCRUD() {
  const [deals, setDeals] = useState([]);
  const [disps, setDisps] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [d, dl] = await Promise.all([dApi.list(), adminApi.deals()]);
      setDisps(d); setDeals(dl.deals);
    } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await adminApi.updateDeal(editing.id, data);
      } else {
        await api.post("/deals", data);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id, t) => { try { await adminApi.deleteDeal(id); toast.success(`Deleted ${t}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-xs text-[#0B0F0C]/60 font-mono-x font-bold">{deals.length} deals</span>
        <button data-testid="cat-deal-new" onClick={() => setEditing({})} className="btn-x btn-primary !text-xs"><Plus size={12} /> New deal</button>
      </div>
      <div className="card-x card-magenta bg-[#FAF6E8] p-2 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#0B0F0C]/55 font-mono-x">
            <tr><th className="p-2">Title</th><th>Shop</th><th>Discount</th><th>Price</th><th>Code</th><th>Expires</th><th>Flags</th><th className="text-right pr-2">Actions</th></tr>
          </thead>
          <tbody>
            {deals.map((d) => (
              <tr key={d.id} data-testid={`cat-deal-row-${d.id}`} className="border-t border-[#0B0F0C]/10 hover:bg-[#FFE74C]/20">
                <td className="p-2 font-display font-bold max-w-[260px] truncate" title={d.title}>{d.title}</td>
                <td className="text-xs">{d.dispensary_name}</td>
                <td>{d.discount_pct}%</td>
                <td className="font-bold">${d.deal_price}</td>
                <td className="font-mono-x text-xs">{d.code}</td>
                <td className="text-[10px] font-mono-x">{(d.expires_at || "").slice(0, 16)}</td>
                <td>{d.is_flash && <span className="sticker sticker-magenta !text-[9px]">FLASH</span>}{d.is_featured && <span className="sticker sticker-green !text-[9px]">★</span>}{d.expired && <span className="sticker !text-[9px]">expired</span>}</td>
                <td className="text-right pr-2 space-x-1">
                  <button data-testid={`cat-deal-edit-${d.id}`} onClick={() => setEditing(d)} className="btn-x btn-ghost !py-1 !px-3 !text-[10px]"><Edit3 size={11} /></button>
                  <button data-testid={`cat-deal-del-${d.id}`} onClick={() => remove(d.id, d.title)} className="btn-x !py-1 !px-3 !text-[10px] !bg-[#FF1A8C] !text-white"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <DealFormModal deal={editing} disps={disps} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function DealFormModal({ deal, disps, onSave, onClose }) {
  const [f, setF] = useState({
    dispensary_id: deal?.dispensary_id || disps[0]?.id || "",
    title: deal?.title || "",
    description: deal?.description || "",
    discount_pct: deal?.discount_pct ?? 20,
    original_price: deal?.original_price ?? 0,
    deal_price: deal?.deal_price ?? 0,
    code: deal?.code || "",
    expires_at: deal?.expires_at?.slice(0, 16) || new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    is_flash: deal?.is_flash ?? false,
  });
  const submit = () => {
    if (!f.title || !f.dispensary_id) { toast.error("Title + shop required"); return; }
    onSave({ ...f, discount_pct: Number(f.discount_pct), original_price: Number(f.original_price), deal_price: Number(f.deal_price), expires_at: new Date(f.expires_at).toISOString() });
  };
  return (
    <div className="fixed inset-0 bg-[#0B0F0C]/60 z-50 flex items-center justify-center p-4 overflow-auto" data-testid="deal-form-modal">
      <div className="card-x card-magenta bg-[#FAF6E8] p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-3xl font-extrabold">{deal?.id ? "Edit" : "New"} deal</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <FormRow label="Title"><Field testid="df-title" placeholder="Flash drop $35 eighths" v={f.title} on={(v) => setF({ ...f, title: v })} /></FormRow>
        <FormRow label="Description"><Field testid="df-desc" placeholder="Description" v={f.description} on={(v) => setF({ ...f, description: v })} /></FormRow>
        <FormRow label="Shop"><Sel testid="df-shop" value={f.dispensary_id} on={(v) => setF({ ...f, dispensary_id: v })} options={disps.map((d) => ({ v: d.id, l: `${d.name} · ${d.state}` }))} /></FormRow>
        <div className="grid sm:grid-cols-3 gap-3">
          <FormRow label="Discount %"><Field testid="df-pct" v={f.discount_pct} on={(v) => setF({ ...f, discount_pct: v })} type="number" /></FormRow>
          <FormRow label="Original $"><Field testid="df-orig" v={f.original_price} on={(v) => setF({ ...f, original_price: v })} type="number" /></FormRow>
          <FormRow label="Deal $"><Field testid="df-deal" v={f.deal_price} on={(v) => setF({ ...f, deal_price: v })} type="number" /></FormRow>
        </div>
        <FormRow label="Code"><Field testid="df-code" placeholder="FLASH35" v={f.code} on={(v) => setF({ ...f, code: v })} /></FormRow>
        <FormRow label="Expires"><input data-testid="df-expires" type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="w-full bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full px-4 h-11 text-sm" /></FormRow>
        <label className="flex items-center gap-2 text-sm mt-2"><input data-testid="df-flash" type="checkbox" checked={f.is_flash} onChange={(e) => setF({ ...f, is_flash: e.target.checked })} /> Flash deal (24h burn)</label>
        <div className="flex gap-3 mt-5">
          <button data-testid="df-cancel" onClick={onClose} className="btn-x btn-ghost flex-1 !text-sm">Cancel</button>
          <button data-testid="df-save" onClick={submit} className="btn-x btn-primary flex-1 !text-sm"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

// ============ ML Insights ============
function InsightsTab() {
  const [data, setData] = useState(null);
  const load = async () => { try { setData(await adminApi.insights()); } catch { toast.error("Failed"); } };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-center py-20">Loading insights…</div>;

  return (
    <div className="space-y-6" data-testid="admin-insights-tab">
      <div className="card-x card-purple bg-[#FAF6E8] p-5">
        <div className="label-overline text-[#FF1A8C] mb-3 flex items-center gap-2"><Brain size={11} strokeWidth={2.4} /> The site is learning</div>
        <p className="text-sm text-[#0B0F0C]/70">Every click, like, claim, comment, and Bud Vision query feeds the taste-profile engine. The Discover page surfaces "For you" + "Trending now" rails powered by this data. As traffic grows, predictions sharpen.</p>
      </div>

      <Section title="Trending products (24h)" overline="Hottest right now">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {data.trending_products.length === 0 && <div className="col-span-full text-sm text-[#0B0F0C]/50 p-6 text-center">No signals in the last 24h yet. As users browse, this rail wakes up.</div>}
          {data.trending_products.map((t, i) => (
            <div key={t.id} className="card-x card-sunset bg-[#FAF6E8] p-3" data-testid={`insight-trend-${t.id}`}>
              <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FF1A8C]">#{i + 1}</div>
              <div className="font-display font-bold text-sm leading-tight mt-1 mb-2 line-clamp-2">{t.name}</div>
              <div className="text-[10px] font-mono-x">{t.category} {t.subtype ? `· ${t.subtype}` : ""}</div>
              <div className="text-[10px] font-mono-x font-bold text-[#FF1A8C] mt-2">🔥 {t.score} · {t.events} events</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Hot deals (24h)" overline="Most-claimed">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {data.hot_deals.length === 0 && <div className="col-span-full text-sm text-[#0B0F0C]/50 p-6 text-center">No deal activity yet.</div>}
          {data.hot_deals.map((d) => (
            <div key={d.id} className="card-x card-magenta bg-[#FAF6E8] p-3" data-testid={`insight-deal-${d.id}`}>
              <div className="font-display font-bold text-sm leading-tight mb-2 line-clamp-2">{d.title}</div>
              <div className="text-[10px] font-mono-x font-bold text-[#FF1A8C]">🔥 {d.score}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Taste graph by state" overline="Where each market leans">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(data.states_taste).map(([st, t]) => (
            <div key={st} className="card-x card-cyan bg-[#FAF6E8] p-4" data-testid={`insight-state-${st}`}>
              <div className="font-display text-2xl font-extrabold">{st}</div>
              <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FF1A8C] mt-2">Top categories</div>
              <div className="text-xs">{t.top_categories.length === 0 ? "(no signals)" : t.top_categories.map(([c, n]) => `${c} (${n})`).join(", ")}</div>
              <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FF1A8C] mt-2">Top subtypes</div>
              <div className="text-xs">{t.top_subtypes.length === 0 ? "(no signals)" : t.top_subtypes.map(([s, n]) => `${s} (${n})`).join(", ")}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Cold products" overline="Zero engagement (7d) — consider removing/discounting">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.cold_products.length === 0 && <div className="col-span-full text-sm text-[#0B0F0C]/50 p-6 text-center">All products are engaging — nothing cold!</div>}
          {data.cold_products.map((p) => (
            <div key={p.id} className="card-x bg-[#FAF6E8] p-3 opacity-70" data-testid={`insight-cold-${p.id}`}>
              <div className="font-display font-bold text-sm leading-tight">{p.name}</div>
              <div className="text-[10px] font-mono-x mt-1">{p.category} · ${p.price}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="mb-3">
      <div className="label-overline text-[#0B0F0C]/60 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
function TagBtn({ tag, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`text-[10px] font-mono-x font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-full border-2 transition-all ${active ? "bg-[#15FF73] border-[#0B0F0C] shadow-[2px_2px_0_#0B0F0C]" : "border-[#0B0F0C]/30 hover:border-[#0B0F0C]"}`}>{tag}</button>
  );
}

// ============ Helpers ============
function Section({ title, overline, children }) {
  return (
    <div>
      <div className="label-overline text-[#FF1A8C] mb-3 flex items-center gap-2">{overline}</div>
      <h3 className="font-display text-2xl font-extrabold mb-3">{title}</h3>
      {children}
    </div>
  );
}
function Stat({ label, value, sub, tone, highlight, testid }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-5`} data-testid={testid}>
      <div className="label-overline text-[#FF1A8C]">{label}</div>
      <div className={`font-display text-3xl font-extrabold mt-2 ${highlight ? "text-[#FF1A8C]" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#0B0F0C]/55 font-mono-x font-bold mt-1 uppercase tracking-widest">{sub}</div>}
    </div>
  );
}
function Field({ placeholder, v, on, testid, type }) {
  return <input data-testid={testid} type={type || "text"} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="w-full bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full focus:bg-white focus:shadow-[3px_3px_0_#0B0F0C] px-4 h-11 text-sm placeholder:text-[#0B0F0C]/40 outline-none transition-all" />;
}
function Sel({ value, on, options, testid }) {
  // options can be array of strings OR array of {v, l}
  const opts = (options || []).map((o) => typeof o === "string" ? { v: o, l: o } : o);
  return (
    <Select value={value} onValueChange={on}>
      <SelectTrigger data-testid={testid} className="rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] h-10 font-bold text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
        {opts.map((o) => <SelectItem key={o.v} value={o.v} className="rounded-lg">{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
